import { FormEvent, useRef, useState } from 'react'
import { ArchiveRestore, ArrowRightLeft, CircleCheck, Plus, ScanBarcode, SearchX, Tag } from 'lucide-react'
import { Alerta, Boton, Campo, Entrada } from '../kit'
import { useBuscarBien, useEditarBien, useMarcarUbicado, useRevertirBaja, useTrasladar } from '../../queries/useInventario'
import { Bien, DatosBien, Recinto } from '../../types/inventario'
import { mensajeDeError } from '../../utils/errores'
import { cuantasFotos, normalizarNumero, tipoDeNumero } from '../../utils/inventario'

type Resultado =
  | { tipo: 'aqui'; bienes: Bien[] }
  | { tipo: 'otro'; bienes: Bien[] }
  | { tipo: 'nuevo'; codigo: string }

const coincide = (b: Bien, codigo: string) => b.numero === codigo || normalizarNumero(b.serie) === codigo

/**
 * Revisar una oficina con la etiqueta en la mano: se escribe (o escanea) el N° de bien
 * o de serie y se da Enter.
 *
 * - Está registrado aquí → se resalta en la lista (y si estaba no ubicado, «Está aquí»).
 * - Está registrado en otra oficina → «Traer aquí» (un traslado).
 * - Está marcado para dar de baja → «Revertir baja» (vuelve a su oficina; si es otra, después se trae).
 * - No existe → «Registrar bien nuevo», con el N° ya puesto.
 *
 * Un lector de códigos actúa como teclado y termina con Enter, así que sirve igual.
 */
export function RevisarPorNumero({
  recinto, bienes, onResaltar, onRegistrar, enDialogo = false,
}: {
  recinto: Recinto
  bienes: Bien[]
  onResaltar: (ids: number[]) => void
  onRegistrar: (inicial: DatosBien) => void
  /** Dentro del diálogo que abre el FAB en el celular: sin la caja de alrededor. */
  enDialogo?: boolean
}) {
  const [texto, setTexto] = useState('')
  const [resultado, setResultado] = useState<Resultado | null>(null)
  const entrada = useRef<HTMLInputElement>(null)
  const buscar = useBuscarBien()
  const trasladar = useTrasladar()
  const ubicar = useMarcarUbicado()
  const editar = useEditarBien()
  const revertir = useRevertirBaja()

  const revisar = (e: FormEvent) => {
    e.preventDefault()
    const codigo = normalizarNumero(texto)
    if (!codigo) return
    setTexto('')
    entrada.current?.focus()
    const aqui = bienes.filter((b) => coincide(b, codigo))
    if (aqui.length) {
      setResultado({ tipo: 'aqui', bienes: aqui })
      onResaltar(aqui.map((b) => b.id))
      return
    }
    buscar.mutate(codigo, {
      onSuccess: (encontrados) => {
        const exactos = encontrados.filter((b) => coincide(b, codigo))
        setResultado(exactos.length ? { tipo: 'otro', bienes: exactos } : { tipo: 'nuevo', codigo })
      },
    })
  }

  const traer = (b: Bien) =>
    trasladar.mutate(
      { bienes: [b.id], destino: recinto.codigo, motivo: 'Encontrado en la revisión de la oficina.' },
      {
        onSuccess: () => {
          setResultado({ tipo: 'aqui', bienes: [{ ...b, recinto, estado: 'ACTIVO' }] })
          onResaltar([b.id])
        },
      },
    )

  const sacarDeBaja = (b: Bien) =>
    revertir.mutate(
      { bienes: [b.id], motivo: 'Encontrado en la revisión de la oficina.' },
      {
        onSuccess: ([vuelto]) => {
          if (vuelto?.recinto?.codigo === recinto.codigo) {
            setResultado({ tipo: 'aqui', bienes: [vuelto] })
            onResaltar([vuelto.id])
          } else if (vuelto) setResultado({ tipo: 'otro', bienes: [vuelto] })
        },
      },
    )

  const error = buscar.error ?? trasladar.error ?? ubicar.error ?? editar.error ?? revertir.error
  return (
    <section className={`revisar${enDialogo ? ' revisar--dialogo' : ''}`} aria-label="Revisar por N°">
      <form className="revisar__form" onSubmit={revisar}>
        <Campo etiqueta="Revisar por N° de bien o de serie" ayuda="Escribe o escanea la etiqueta y presiona Enter.">
          <Entrada
            ref={entrada}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Ej.: 10953-1 o DCC-0058"
            autoComplete="off"
            autoCapitalize="characters"
            enterKeyHint="search"
          />
        </Campo>
        <Boton type="submit" icono={ScanBarcode} cargando={buscar.isPending}>Revisar</Boton>
      </form>

      {resultado?.tipo === 'aqui' &&
        resultado.bienes.map((b) => (
          <div key={b.id} className="revisar__resultado revisar__resultado--ok" role="status">
            <CircleCheck className="dcc-i" size={20} aria-hidden />
            <span className="revisar__texto">
              <strong>{b.numero || 'Sin N°'}</strong> {b.descripcion} <span className="texto-secundario">({cuantasFotos(b.n_fotos).toLowerCase()})</span>{' '}
              está registrado en esta oficina.
            </span>
            {b.estado === 'NO_UBICADO' && (
              <Boton tam="sm" onClick={() => ubicar.mutate({ id: b.id, recinto: recinto.codigo }, { onSuccess: () => setResultado(null) })} cargando={ubicar.isPending}>
                Está aquí
              </Boton>
            )}
            {b.etiqueta_pendiente && (
              <Boton tam="sm" variante="outlined" icono={Tag} onClick={() => editar.mutate({ id: b.id, datos: { etiqueta_pendiente: false } }, { onSuccess: () => setResultado(null) })} cargando={editar.isPending}>
                Etiqueta pegada
              </Boton>
            )}
          </div>
        ))}

      {resultado?.tipo === 'otro' &&
        resultado.bienes.map((b) => (
          <div key={b.id} className="revisar__resultado revisar__resultado--aviso" role="status">
            <ArrowRightLeft className="dcc-i" size={20} aria-hidden />
            <span className="revisar__texto">
              <strong>{b.numero || b.serie}</strong> {b.descripcion} <span className="texto-secundario">({cuantasFotos(b.n_fotos).toLowerCase()})</span>{' '}
              {b.estado === 'BAJA'
                ? `está marcado para dar de baja${b.recinto ? ` (era de ${b.recinto.nombre}, ${b.recinto.codigo})` : ''}.`
                : b.recinto
                  ? `está registrado en ${b.recinto.nombre} (${b.recinto.codigo})${b.estado === 'NO_UBICADO' ? ', como no ubicado' : ''}.`
                  : 'no tiene oficina en el sistema: nadie lo había encontrado.'}
            </span>
            {b.estado === 'BAJA' ? (
              <Boton tam="sm" variante="outlined" icono={ArchiveRestore} onClick={() => sacarDeBaja(b)} cargando={revertir.isPending}>
                Revertir baja
              </Boton>
            ) : (
              <Boton tam="sm" onClick={() => traer(b)} cargando={trasladar.isPending}>Traer aquí</Boton>
            )}
          </div>
        ))}

      {resultado?.tipo === 'nuevo' && (
        <div className="revisar__resultado revisar__resultado--nuevo" role="status">
          <SearchX className="dcc-i" size={20} aria-hidden />
          <span className="revisar__texto">
            Ningún bien tiene el N° o la serie <strong>{resultado.codigo}</strong>.
          </span>
          <Boton
            tam="sm"
            icono={Plus}
            onClick={() => {
              onRegistrar(tipoDeNumero(resultado.codigo) === null ? { serie: resultado.codigo } : { numero: resultado.codigo })
              setResultado(null)
            }}
          >
            Registrar bien nuevo
          </Boton>
        </div>
      )}

      {error && <Alerta tipo="error">{mensajeDeError(error)}</Alerta>}
    </section>
  )
}
